import { useMemo, useState } from 'react';
import { normaliseText, nameInput, tenDigits, toggleWithWedding } from '../../utils/helpers';
import { contactsSupported, pickContactFromDevice } from '../../utils/contacts';

const ten = tenDigits;
const blank = (head) => ({
  Surname: head?.Surname || '',
  GuestName: '',
  MobileNumber: '',
  Side: head?.Side || 'Common',
  Visibility: head?.Visibility || 'Private',
  Remarks: '',
});

// "Add member to family" form logic — shared by FamilyMemberForm.desktop / .mobile.
//   mode 'new'      → creates a guest inheriting the head's functions/side/visibility
//                     (a matching phone moves that existing guest into the family instead)
//   mode 'existing' → attaches an individual guest already in the list
// The functions the HEAD is invited to: the menu a new member picks from.
const headFunctionIds = (head) => {
  if (head?.FunctionIds) return String(head.FunctionIds).split(',').map(v => v.trim()).filter(Boolean);
  return head?.FunctionId ? [String(head.FunctionId)] : [];
};

export function useFamilyMemberForm({ head, rows, functions = [], onClose, onSave, isMarriage }) {
  const [mode, setMode] = useState('new');
  const [form, setForm] = useState(() => blank(head));
  const [existingGuestId, setExistingGuestId] = useState('');
  const [saving, setSaving] = useState(false);
  // A member may come to fewer functions than the head - start with all of
  // the head's, untick the ones this person is not invited to.
  const headFns = useMemo(() => headFunctionIds(head), [head]);
  const [fnIds, setFnIds] = useState(headFns);
  const familyFunctions = useMemo(
    () => headFns.map(id => ({
      FunctionId: id,
      FunctionName: functions.find(f => String(f.FunctionId) === String(id))?.FunctionName || `Function ${id}`,
    })),
    [headFns, functions]
  );
  // Wedding rule within the head's functions: ticking another one ticks the
  // Wedding too, unless it was unticked by hand for this member.
  const [weddingOff, setWeddingOff] = useState(false);
  const headPool = useMemo(() => functions.filter(f => headFns.includes(String(f.FunctionId))), [functions, headFns]);
  const toggleFunction = (id) => {
    const res = toggleWithWedding(fnIds, id, headPool, weddingOff);
    setWeddingOff(res.weddingOff);
    setFnIds(res.ids);
  };

  // Individual guests (no family yet) other than the head, deduped by GuestId.
  const available = useMemo(() => {
    const seen = new Set();
    return rows.filter(row => {
      if (Number(row.FamilyId || 0) !== 0 || String(row.GuestId) === String(head.GuestId)) return false;
      if (seen.has(String(row.GuestId))) return false;
      seen.add(String(row.GuestId));
      return true;
    });
  }, [rows, head]);

  const setF = (key, value) => setForm(current => ({
    ...current,
    [key]: key === 'MobileNumber' ? ten(value)
      : ['Surname', 'GuestName'].includes(key) ? nameInput(value)
        : value,
  }));

  // Same phone book as everywhere else; a number is optional here too.
  const canImport = contactsSupported();
  const importContact = async () => {
    try {
      const c = await pickContactFromDevice();
      if (!c) return;
      setForm(f => ({
        ...f,
        GuestName: normaliseText(c.name) || f.GuestName,
        Surname: normaliseText(c.surname) || f.Surname,   // usually the head's already
        MobileNumber: c.mobile || f.MobileNumber,
      }));
    } catch { /* permission refused - typing still works */ }
  };

  // Names already in the list, so one family is spelt one way.
  const knownNames = useMemo(
    () => [...new Set(rows.map(r => String(r.GuestName || '').trim()).filter(Boolean))].sort(),
    [rows]
  );

  const canSubmit = mode === 'existing'
    ? !!existingGuestId
    : !!form.GuestName.trim() && !!fnIds.length && (!form.MobileNumber || /^\d{10}$/.test(form.MobileNumber));

  const submit = async () => {
    if (!canSubmit) return;
    const payload = mode === 'existing'
      ? { ExistingGuestId: existingGuestId }
      : { ...form, Side: isMarriage ? form.Side : 'Common', FunctionIds: fnIds };
    setSaving(true);
    try {
      await onSave(payload);
      onClose();
    } catch {
      // The owning hook shows the backend validation message as a toast.
    } finally {
      setSaving(false);
    }
  };

  return { mode, setMode, form, setF, existingGuestId, setExistingGuestId, available, saving, canSubmit, submit,
    canImport, importContact, knownNames, familyFunctions, fnIds, toggleFunction };
}
